import { useTranslation } from 'react-i18next'
import { useLoaderData } from 'react-router'
import {
	getBlogCategoryBySlug,
	getLocalizedField,
	getPublishedPosts,
	type SupportedLocale,
} from '#app/utils/blog.server.ts'
import { useLang, getHreflangLinks } from '#app/utils/i18n.ts'
import { getDomainUrl } from '#app/utils/misc.tsx'
import { BlogListing } from './+blog-components/blog-listing.tsx'
import { type Route } from './+types/blog.category.$categorySlug.ts'

const PAGE_SIZE = 12

const translations = {
	postsInCategory: {
		de: (count: number, name: string) =>
			`${count} ${count === 1 ? 'Beitrag' : 'Beiträge'} in «${name}»`,
		fr: (count: number, name: string) =>
			`${count} ${count === 1 ? 'article' : 'articles'} dans «${name}»`,
		en: (count: number, name: string) =>
			`${count} ${count === 1 ? 'post' : 'posts'} in "${name}"`,
	},
} as const

export async function loader({ params, request }: Route.LoaderArgs) {
	const category = await getBlogCategoryBySlug(params.categorySlug)

	if (!category) {
		throw new Response('Not found', { status: 404 })
	}

	const url = new URL(request.url)
	const page = Math.max(1, Number(url.searchParams.get('page')) || 1)

	const { posts, totalCount, totalPages, currentPage } =
		await getPublishedPosts({
			page,
			pageSize: PAGE_SIZE,
			categorySlug: params.categorySlug,
		})

	const locale = (params.lang || 'de') as SupportedLocale
	const categoryName = getLocalizedField(category, 'name', locale)
	const siteUrl = process.env.APP_URL || getDomainUrl(request)

	return {
		category,
		categoryName,
		categorySlug: category.slug,
		posts,
		totalCount,
		totalPages,
		currentPage,
		siteUrl,
	}
}

export const meta: Route.MetaFunction = ({ data, params }) => {
	if (!data) {
		return [{ title: '404 | Blog | Open Sourced' }]
	}

	const tags: ReturnType<Route.MetaFunction> = [
		{ title: `${data.categoryName} | Blog | Open Sourced` },
		{
			name: 'description',
			content: `${data.categoryName}: Blog-Beiträge auf Open Sourced`,
		},
	]

	const pathname = `/${params.lang || 'de'}/blog/category/${data.categorySlug}`
	const hreflangLinks = getHreflangLinks(pathname, data.siteUrl)
	tags.push(...hreflangLinks)

	return tags
}

export default function BlogCategoryPage() {
	const {
		posts,
		totalCount,
		totalPages,
		currentPage,
		categoryName,
		categorySlug,
	} = useLoaderData<typeof loader>()
	const lang = useLang()
	const { t } = useTranslation()

	return (
		<BlogListing
			title={categoryName}
			summary={translations.postsInCategory[lang](totalCount, categoryName)}
			empty={{
				title: t('blog.emptyCategory.title'),
				description: t('blog.emptyCategory.description'),
			}}
			posts={posts}
			currentPage={currentPage}
			totalPages={totalPages}
			basePath={`/blog/category/${categorySlug}`}
		/>
	)
}
